/**
 * Helper functions for receipt processing
 * Based on Flutter ReceiptHelper
 */

/**
 * Check if text contains Arabic characters
 */
export function isArabicText(text: string): boolean {
  return /[\u0600-\u06FF]/.test(text);
}

/**
 * Check if text contains English characters
 */
export function isEnglishText(text: string): boolean {
  return /[a-zA-Z]/.test(text);
}

/**
 * Get correct price from item data - returns total price for the item (price * quantity)
 */
export function getCorrectPrice(item: any): string {
  // First try to get total_price (price for all quantity)
  const totalPrice = parseFloat(
    String(
      item.total_price ??
      item.totalPrice ??
      item.total_unit_price ??
      item.totalUnitPrice ??
      item.item_total ??
      0.0
    )
  )

  if (totalPrice > 0) {
    return totalPrice.toFixed(2)
  }

  // If no total_price, calculate from unit price * quantity
  const unitPrice = parseFloat(
    String(
      item.price ??
      item.unit_price ??
      item.item_price ??
      item.product_price ??
      0.0
    )
  )

  const quantity = parseFloat(String(item.quantity ?? '1')) || 1.0

  if (unitPrice > 0 && quantity > 0) {
    return (unitPrice * quantity).toFixed(2)
  }

  return '0.00'
}

/**
 * Translate payment method based on locale
 */
export function translatePaymentMethod(
  method: string,
  locale: string = 'ar'
): string {
  const methodLower = method.toLowerCase();

  if (locale === 'en') {
    // Translate Arabic payment methods to English
    switch (methodLower) {
      case 'كاش':
      case 'نقدي':
        return 'Cash';
      case 'أونلاين':
      case 'شبكة':
        return 'Online';
      case 'فيزا':
        return 'Visa';
      case 'محفظة':
        return 'Wallet';
      case 'دفع جزئي':
        return 'Partial Payment';
      default:
        return method;
    }
  } else {
    // Keep Arabic as is or translate English to Arabic
    // Handle network/card first to ensure it's displayed as "شبكة"
    if (methodLower.includes('network') || methodLower.includes('card') || methodLower === 'شبكة') {
      return 'شبكة';
    }
    
    // Handle wallet - ensure it's displayed as "محفظة"
    if (methodLower.includes('wallet') || methodLower === 'محفظة') {
      return 'محفظة';
    }
    
    switch (methodLower) {
      case 'cash':
        return 'كاش';
      case 'online':
        return 'أونلاين';
      case 'visa':
        return 'فيزا';
      case 'partial payment':
      case 'partial':
        return 'دفع جزئي';
      default:
        // If method is already in Arabic, return as is
        if (isArabicText(method)) {
          return method;
        }
        return method;
    }
  }
}

/**
 * Get appropriate text alignment based on text language
 */
export function getTextAlignment(text: string): 'left' | 'right' {
  return isArabicText(text) ? 'right' : 'left';
}

